// TextEditor.tsx
import React, { useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import { useTranslation } from "react-i18next";
import { useDroppable, useDndMonitor } from "@dnd-kit/core";
import "./AutoRuleBlot";
import { AutoRuleToolbar } from "./AutoRuleToolbar";
import "react-quill/dist/quill.snow.css";

const AutoRuleEditor = ({ onTokenDrop, editorValue }) => {
  const quillRef = useRef(null);
  const [t] = useTranslation();

  const { setNodeRef } = useDroppable({
    id: "editor-drop-zone",
  });

  const insertAutoRule = (rule) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (range) {
      if (range.index > 0) {
        const [prevBlot] = quill.getLeaf(range.index - 1);
        if (prevBlot) {
          quill.insertText(range.index, " ");
        }
      }
      quill.insertEmbed(
        range.index,
        "rule",
        {
          name: rule.name,
          id: rule.id,
        },
        "user"
      );
      quill.insertText(range.index + 1, " ");
      quill.setSelection(range.index + 2, 0);
    }
  };
  useDndMonitor({
    onDragEnd(event) {
      if (
        event.over?.id === "editor-drop-zone" &&
        event.active?.data?.current?.id
      ) {
        const rule = event.active.data.current;
        insertAutoRule(rule);
      }
    },
  });

  useEffect(() => {
    if (!quillRef.current) return;
    const quill = quillRef.current?.getEditor();
    const button = document.getElementById("insert-rule-delimeter");

    if (button && quill) {
      button.onclick = () => {
        const range = quill.getSelection(true);
        if (range) {
          quill.insertEmbed(
            range.index,
            "ql-rule-delimeter",
            t("versionsForm.autorule.delimeterText"),
            "user"
          );
          quill.insertText(range.index + 1, " ");
          quill.setSelection(range.index + 2, 0);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (quillRef.current && editorValue) {
      const quill = quillRef.current.getEditor();
      quill.clipboard.addMatcher("span", (node, delta) => {
        if (node.classList.contains("autorule-token-blot")) {
          const id = node.getAttribute("data-id");
          const name = node.getAttribute("data-rule");

          return {
            ops: [
              {
                insert: {
                  rule: {
                    id,
                    name,
                  },
                },
              },
              { insert: " " },
            ],
          };
        }
        if (node.classList.contains("rule-deliemter-blot")) {
          return {
            ops: [
              {
                insert: {
                  "ql-rule-delimeter": t("versionsForm.autorule.delimeterText"),
                },
              },
              { insert: " " },
            ],
          };
        }
        return delta;
      });

      quill.clipboard.dangerouslyPasteHTML(editorValue);
    }
  }, []);

  const handleChange = () => {
    const quill = quillRef.current?.getEditor();
    if (quill && onTokenDrop) {
      const html = quill.root.innerHTML;
      onTokenDrop(html);
    }
  };

  return (
    <div className="custom-quill-editor" ref={setNodeRef}>
      <AutoRuleToolbar />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        onChange={onTokenDrop}
        modules={{
          toolbar: {
            container: "#toolbar",
          },
        }}
        placeholder="Drop rules from left panel to create custom message"
      />
    </div>
  );
};

export default AutoRuleEditor;
